import { ITagImage, ITagImageStatus, Icons } from '@tecalisdev/infinity';
import { MODE } from '../models/user-identification';

/** Métodos del picker Flexible (NIF-802 + EUDI NIF-960). Incluye escaneo para volver sin «Volver atrás». */
export type OcrFlexibleMethod = 'midni' | 'eudi' | 'upload' | 'photo' | 'scan';

/** Pantallas overlay del flujo Flexible (además del escaneo por defecto). */
export type OcrFlexibleScreen = 'picker' | 'upload' | 'photo';

/**
 * True cuando el journey debe montar `app-ocr` (escaneo + alternativas Flexible).
 * `auto` y `flexible` comparten captura activa; `manual` sigue en `app-manual-ocr`.
 */
export function isOcrScanCaptureMode(mode: MODE | string | null | undefined): boolean {
	return mode === MODE.AUTO || mode === MODE.FLEXIBLE || mode === 'auto' || mode === 'flexible';
}

/** True si `OcrConfig.mode === flexible`. */
export function isOcrFlexibleMode(mode: MODE | string | null | undefined): boolean {
	return mode === MODE.FLEXIBLE || mode === 'flexible';
}

/**
 * Alternativas al escaneo (link bajo la cámara).
 * Con Flexible: siempre `upload` + `photo`; `midni` / `eudi` si el perfil lo habilita.
 * Sin Flexible: solo `midni` si está activo (EUDI ACTIVA usa link dedicado `OCR.EUDI.PREFER`).
 */
export function resolveOcrCaptureAlternatives(options: {
	flexible: boolean;
	midniEnabled: boolean;
	eudiEnabled?: boolean;
}): OcrFlexibleMethod[] {
	const methods: OcrFlexibleMethod[] = [];
	// NIF-960 / Figma: EUDI antes que MiDNI cuando ambos están activos.
	if (options.flexible && options.eudiEnabled) {
		methods.push('eudi');
	}
	if (options.midniEnabled) {
		methods.push('midni');
	}
	if (options.flexible) {
		methods.push('upload');
		methods.push('photo');
	}
	return methods;
}

/**
 * Métodos del picker Flexible (Figma: EUDI → MiDNI → Adjuntar → Sacar foto → Escaneo automático).
 * Con 4 métodos (p. ej. EUDI sin MiDNI): EUDI ocupa el primer hueco.
 */
export function resolveOcrFlexiblePickerMethods(options: {
	flexible: boolean;
	midniEnabled: boolean;
	eudiEnabled?: boolean;
}): OcrFlexibleMethod[] {
	if (!options.flexible) {
		return resolveOcrCaptureAlternatives(options);
	}
	return [...resolveOcrCaptureAlternatives(options), 'scan'];
}

/**
 * Columnas del grid picker (NIF-960 / Carmen): exactamente 4 → fila `grid-e4`; resto → 3.
 */
export function resolveOcrFlexiblePickerCols(methodCount: number): number {
	return methodCount === 4 ? 4 : 3;
}

/**
 * Clave i18n del link bajo el escaneo, o `null` si no hay alternativas.
 * 1 método MiDNI/EUDI/upload/photo → copy concreto; 2+ → «otro método» (picker).
 */
export function ocrCapturePreferLinkI18nKey(alternatives: OcrFlexibleMethod[]): string | null {
	const withoutScan = alternatives.filter((m) => m !== 'scan');
	if (withoutScan.length === 0) {
		return null;
	}
	if (withoutScan.length === 1) {
		if (withoutScan[0] === 'midni') {
			return 'OCR.MIDNI.PREFER';
		}
		if (withoutScan[0] === 'eudi') {
			return 'OCR.EUDI.PREFER';
		}
		if (withoutScan[0] === 'upload') {
			return 'OCR.FLEXIBLE.PREFER_UPLOAD';
		}
		if (withoutScan[0] === 'photo') {
			return 'OCR.FLEXIBLE.PREFER_PHOTO';
		}
	}
	return 'OCR.FLEXIBLE.PREFER_OTHER_METHOD';
}

/** TagImage del método (logo MiDNI/EUDI; colores notice/control/level como Figma). */
export function ocrFlexibleMethodTagImage(method: OcrFlexibleMethod, index: number): ITagImage {
	const statuses: ITagImageStatus[] = ['notice', 'control', 'default', 'level'];
	const status = statuses[index % statuses.length] ?? 'default';
	switch (method) {
		case 'midni':
			return { img: 'assets/images/midni-logo.png', status: 'default' };
		case 'eudi':
			return { img: 'assets/images/eudi-wallet.png', status: 'default' };
		case 'upload':
			return { icon: 'icon-upload' as Icons, status: 'info' };
		case 'photo':
			return { icon: 'icon-camera' as Icons, status: 'default' };
		case 'scan':
			return { icon: 'icon-AI' as Icons, status: 'notice' };
	}
}

/**
 * Extensiones aceptadas en adjuntar (imagen + PDF).
 * WebP (NIF-921): se acepta en front y `normalizeOcrFlexibleUploadDataUrl` lo pasa a JPEG
 * antes del POST (mismo contrato que jpg/png para el back).
 * PDF: el back convierte vía PythonUtils.
 */
export const OCR_FLEXIBLE_UPLOAD_EXTENSIONS: readonly string[] = ['.jpg', '.jpeg', '.png', '.webp', '.pdf'] as const;

export const OCR_FLEXIBLE_UPLOAD_MAX_FILES = 2;
export const OCR_FLEXIBLE_UPLOAD_MAX_BYTES = 10 * 1024 * 1024; // 10 MB

/** MIME permitidos alineados a extensiones. */
export function isOcrFlexibleUploadFileAllowed(file: File): boolean {
	const name = (file.name || '').toLowerCase();
	const mime = (file.type || '').toLowerCase();
	const byExt = OCR_FLEXIBLE_UPLOAD_EXTENSIONS.some((ext) => name.endsWith(ext));
	const byMime =
		mime === 'image/jpeg' ||
		mime === 'image/jpg' ||
		mime === 'image/png' ||
		mime === 'image/webp' ||
		mime === 'application/pdf';
	return byExt || byMime;
}

/** Lee un File como data-URL (base64 con prefijo data:). */
export function readFileAsDataUrl(file: File): Promise<string> {
	return new Promise((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result ?? ''));
		reader.onerror = () => reject(reader.error ?? new Error('FileReader failed'));
		reader.readAsDataURL(file);
	});
}

/** SHA-256 hex del contenido del File (detectar mismo archivo dos veces, NIF-921). */
export async function hashOcrFlexibleUploadFile(file: File): Promise<string> {
	const buffer = await file.arrayBuffer();
	const digest = await crypto.subtle.digest('SHA-256', buffer);
	return Array.from(new Uint8Array(digest))
		.map((b) => b.toString(16).padStart(2, '0'))
		.join('');
}

/**
 * True si hay al menos dos ficheros con el mismo hash de contenido.
 * No detecta la misma cara en fotos distintas (eso lo decide el back).
 */
export async function hasDuplicateOcrFlexibleUploadFiles(files: readonly File[]): Promise<boolean> {
	if (files.length < 2) {
		return false;
	}
	const seen = new Set<string>();
	for (const file of files) {
		const hash = await hashOcrFlexibleUploadFile(file);
		if (seen.has(hash)) {
			return true;
		}
		seen.add(hash);
	}
	return false;
}

/** Valores de `capture_method` enviados en POST /ocr (alineados a new_pwcs / Admin). */
export type OcrCaptureMethodValue = 'UPLOAD' | 'PHOTO' | 'SCAN';

/**
 * Método real de captura para telemetría Admin (NIF-921).
 * MiDNI/EUDI los escribe el back en sus flujos.
 */
export function resolveOcrCaptureMethod(options: {
	flexibleUploadSession: boolean;
	flexibleScreen: OcrFlexibleScreen | null;
	isModeSemiManual: boolean;
}): OcrCaptureMethodValue {
	if (options.flexibleUploadSession || options.flexibleScreen === 'upload') {
		return 'UPLOAD';
	}
	if (options.flexibleScreen === 'photo' || options.isModeSemiManual) {
		return 'PHOTO';
	}
	return 'SCAN';
}

/**
 * JPEG/PNG/WebP → JPEG acotado (mismo espíritu que el frame del escaneo).
 * Evita POST enormes que PHP/Laravel rechazan sin `error_code` → SERVER_ERROR en journey.
 * PDF y otros data-URI se dejan igual (el back ya convierte PDF).
 */
export function normalizeOcrFlexibleUploadDataUrl(
	dataUrl: string,
	maxEdge: number = 1920,
	jpegQuality: number = 0.92,
): Promise<string> {
	if (!dataUrl.startsWith('data:image/')) {
		return Promise.resolve(dataUrl);
	}
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const longest = Math.max(img.width, img.height) || 1;
			const scale = Math.min(1, maxEdge / longest);
			const width = Math.max(1, Math.round(img.width * scale));
			const height = Math.max(1, Math.round(img.height * scale));
			const canvas = document.createElement('canvas');
			canvas.width = width;
			canvas.height = height;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				resolve(dataUrl);
				return;
			}
			ctx.drawImage(img, 0, 0, width, height);
			resolve(canvas.toDataURL('image/jpeg', jpegQuality));
		};
		img.onerror = () => resolve(dataUrl);
		img.src = dataUrl;
	});
}

export type OcrFlexibleCropBox = {
	originX: number;
	originY: number;
	width: number;
	height: number;
};

/** Recorte JPEG de una región (bbox MediaPipe u otra) con padding relativo. */
export function cropOcrFlexibleUploadDataUrl(
	dataUrl: string,
	box: OcrFlexibleCropBox,
	padRatio: number = 0.04,
	jpegQuality: number = 0.92,
): Promise<string> {
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const padX = Math.round(box.width * padRatio);
			const padY = Math.round(box.height * padRatio);
			const x = Math.max(0, Math.floor(box.originX - padX));
			const y = Math.max(0, Math.floor(box.originY - padY));
			const w = Math.min(img.width - x, Math.ceil(box.width + padX * 2));
			const h = Math.min(img.height - y, Math.ceil(box.height + padY * 2));
			if (w < 8 || h < 8) {
				resolve(dataUrl);
				return;
			}
			const canvas = document.createElement('canvas');
			canvas.width = w;
			canvas.height = h;
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				resolve(dataUrl);
				return;
			}
			ctx.drawImage(img, x, y, w, h, 0, 0, w, h);
			resolve(canvas.toDataURL('image/jpeg', jpegQuality));
		};
		img.onerror = () => resolve(dataUrl);
		img.src = dataUrl;
	});
}

/**
 * NIF-1082: true si el aspect sugiere anverso+reverso en la misma imagen
 * (muy ancha o muy alta). Ya no se parte: se rechaza con error específico.
 */
export function looksLikeDualLayoutUploadDataUrl(
	dataUrl: string,
	aspectThreshold: number = 2.15,
): Promise<boolean> {
	if (!dataUrl.startsWith('data:image/')) {
		return Promise.resolve(false);
	}
	return new Promise((resolve) => {
		const img = new Image();
		img.onload = () => {
			const w = img.width;
			const h = img.height;
			if (!w || !h) {
				resolve(false);
				return;
			}
			const ratio = w / h;
			resolve(ratio >= aspectThreshold || 1 / ratio >= aspectThreshold);
		};
		img.onerror = () => resolve(false);
		img.src = dataUrl;
	});
}

